import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
} from "@fluentui/react-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMassCreateActivityDrafts } from "../../hooks/useActivities";
import { useMassCreateImageDrafts } from "../../hooks/useImages";
import { useMassCreateToolDrafts } from "../../hooks/useTools";

export type MassCreateDraftEntityType = "activity" | "tool" | "image";

interface MassCreateDraftResultItem {
  versionId: string;
  success: boolean;
  title?: string;
  errorMessage?: string;
  errorCode?: number;
  errorTranslationKey?: string;
}

const useStyles = makeStyles({
  dialogSurface: {
    width: "400px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
  summaryText: {
    fontWeight: "600",
    marginBottom: "12px",
  },
  resultsContainer: {
    marginTop: "16px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  resultItem: {
    padding: "8px",
    marginBottom: "4px",
    borderRadius: "4px",
    fontSize: "14px",
  },
  successItem: {
    backgroundColor: "#dff6dd",
    color: "#0f5132",
  },
  errorItem: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
  },
  errorContainer: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px",
    borderRadius: "4px",
    marginBottom: "16px",
    border: "1px solid #f5c6cb",
  },
  errorTitle: {
    fontWeight: "600",
    marginBottom: "8px",
  },
  iconWrapper: {
    display: "flex",
    gap: "8px",
  },
  icon: {
    flexShrink: 0,
  },
});

interface MassCreateDraftButtonProps {
  versionIds: string[];
  disabled?: boolean;
  entityType: MassCreateDraftEntityType;
  onSuccess: (createdVersionIds: string[]) => void;
}

export const MassCreateDraftButton = (props: MassCreateDraftButtonProps) => {
  const { versionIds, disabled, entityType, onSuccess } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    mutateAsync: massCreateActivityDrafts,
    isPending: isCreatingActivityDrafts,
  } = useMassCreateActivityDrafts();
  const {
    mutateAsync: massCreateToolDrafts,
    isPending: isCreatingToolDrafts,
  } = useMassCreateToolDrafts();
  const {
    mutateAsync: massCreateImageDrafts,
    isPending: isCreatingImageDrafts,
  } = useMassCreateImageDrafts();

  const isPending =
    entityType === "activity"
      ? isCreatingActivityDrafts
      : entityType === "tool"
        ? isCreatingToolDrafts
        : isCreatingImageDrafts;

  const [open, setOpen] = useState<boolean>(false);
  const [results, setResults] = useState<MassCreateDraftResultItem[] | null>(
    null
  );
  const [createdVersionIds, setCreatedVersionIds] = useState<string[]>([]);
  const [summary, setSummary] = useState<{
    successfulCount: number;
    failedCount: number;
    totalCount: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onClickCreateDrafts = async () => {
    try {
      setError(null);

      if (entityType === "activity") {
        const response = await massCreateActivityDrafts({ versionIds });
        applyResponse(
          response.results.map((r) => ({
            // Drafts list rows are keyed by the new draft's own versionId, not the published item's
            versionId: r.success ? (r.draftActivity?.versionId ?? r.versionId) : r.versionId,
            success: r.success,
            title: r.draftActivity?.title,
            errorMessage: r.errorMessage,
            errorCode: r.errorCode,
            errorTranslationKey: r.errorTranslationKey,
          })),
          response.successfulCount,
          response.failedCount,
          response.totalCount
        );
      } else if (entityType === "tool") {
        const response = await massCreateToolDrafts({
          versionIds,
          oldVersion: false,
        });
        applyResponse(
          response.results.map((r) => ({
            versionId: r.success ? (r.draftTool?.versionId ?? r.versionId) : r.versionId,
            success: r.success,
            title: r.draftTool?.title,
            errorMessage: r.errorMessage,
            errorCode: r.errorCode,
            errorTranslationKey: r.errorTranslationKey,
          })),
          response.successfulCount,
          response.failedCount,
          response.totalCount
        );
      } else {
        const response = await massCreateImageDrafts({ versionIds });
        applyResponse(
          response.results.map((r) => ({
            versionId: r.success ? (r.draftImage?.versionId ?? r.versionId) : r.versionId,
            success: r.success,
            title: r.draftImage?.title,
            errorMessage: r.errorMessage,
            errorCode: r.errorCode,
            errorTranslationKey: r.errorTranslationKey,
          })),
          response.successfulCount,
          response.failedCount,
          response.totalCount
        );
      }
    } catch (caughtError) {
      let errorMessage = t("massCreateDraftGeneralError");

      if (
        caughtError &&
        typeof caughtError === "object" &&
        "message" in caughtError
      ) {
        errorMessage = caughtError.message as string;
      } else if (typeof caughtError === "string") {
        errorMessage = caughtError;
      }

      setError(errorMessage);
      setOpen(true);
    }
  };

  const applyResponse = (
    resultItems: MassCreateDraftResultItem[],
    successfulCount: number,
    failedCount: number,
    totalCount: number
  ) => {
    const succeededVersionIds = resultItems
      .filter((r) => r.success)
      .map((r) => r.versionId);

    setCreatedVersionIds(succeededVersionIds);

    if (failedCount > 0) {
      setResults(resultItems);
      setSummary({ successfulCount, failedCount, totalCount });
      setOpen(true);
    } else {
      onSuccess(succeededVersionIds);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setResults(null);
    setSummary(null);
    setError(null);
    onSuccess(createdVersionIds);
  };

  return (
    <>
      <Button
        size="medium"
        disabled={disabled || versionIds.length === 0 || isPending}
        onClick={onClickCreateDrafts}
      >
        {t("createDraft")}
      </Button>
      <Dialog open={open}>
        <DialogSurface
          className={classes.dialogSurface}
          aria-describedby={undefined}
        >
          <DialogBody>
            <DialogTitle>{t("massCreateDraftResultsTitle")}</DialogTitle>
            <DialogContent>
              <div className={classes.content}>
                {error ? (
                  <div className={classes.errorContainer}>
                    <div className={classes.errorTitle}>
                      {t("massCreateDraftErrorTitle")}
                    </div>
                    <div>{error}</div>
                  </div>
                ) : summary && results ? (
                  <>
                    <div className={classes.summaryText}>
                      {t("massCreateDraftSummary", {
                        successful: summary.successfulCount,
                        failed: summary.failedCount,
                        total: summary.totalCount,
                      })}
                    </div>
                    <div className={classes.resultsContainer}>
                      {results.map((result) => (
                        <div
                          key={result.versionId}
                          className={`${classes.resultItem} ${
                            result.success
                              ? classes.successItem
                              : classes.errorItem
                          }`}
                        >
                          {result.success ? (
                            <div className={classes.iconWrapper}>
                              <CheckmarkCircle20Regular
                                className={classes.icon}
                              />
                              {t("massCreateDraftItemSuccess", {
                                title: result.title || t("untitledActivity"),
                              })}
                            </div>
                          ) : (
                            <div className={classes.iconWrapper}>
                              <DismissCircle20Regular
                                className={classes.icon}
                              />
                              {t("massCreateDraftItemError", {
                                error: result.errorTranslationKey
                                  ? t(result.errorTranslationKey, {
                                      defaultValue:
                                        result.errorMessage ||
                                        t("unknownError"),
                                    })
                                  : result.errorMessage || t("unknownError"),
                              })}
                              {result.errorCode && (
                                <span>
                                  {" "}
                                  ({t("errorCode")}: {result.errorCode})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button onClick={handleClose} appearance="primary">
                  {t("close")}
                </Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default MassCreateDraftButton;
